import {
	TASK_CATEGORIES,
	TASK_STATUS,
	TASK_STATUS_FOR_EXTERNAL_CLINIC,
	TASK_STATUS_FOR_INTERNAL_CLINIC,
	TASK_TYPES,
	TaskPriorityConfig,
	TaskWorkflowProcessesConfig,
	TaskWorkflowStagesConfig,
} from './task'
import {
	CalendarEventCategoryConfig,
	CalendarEventCategoryConfigV2,
	CalendarEventStatusConfig,
	CalendarEventTypeConfig,
} from './calendar'
import { ReportPeriod, ReportType } from './reports'
import {
	AGE_GROUPS,
	COMORBIDITIES,
	COUNTRIES,
	DIABETES_TYPES,
	GENDERS,
	LANGUAGES_FULL,
	REMINDER_VALUES,
} from './common'

export interface SystemConfigEntity {
	_id: string
	entity: string
	data: SystemConfigSystem | object
}

export interface SystemConfigCountryData {
	id: string
	name: COUNTRIES
	['ui_text']: string
	states: Array<{
		name: string
		abbreviation: string
	}>
}

export interface SystemConfigSystem {
	[key: string]: unknown
	taskPriorities: Array<TaskPriorityConfig>
	taskStatuses: Array<SystemConfigValueParam<string, TASK_STATUS>>
	taskReminders: Array<SystemConfigReminder>
	taskTypes: Array<SystemConfigValueParam<string, TASK_TYPES>>
	taskCategories: Array<SystemConfigValueParam<number, TASK_CATEGORIES>>
	workflowProcesses: Array<TaskWorkflowProcessesConfig>
	workflowStages: Array<TaskWorkflowStagesConfig>
	eventTypes: Array<CalendarEventTypeConfig>
	eventCategories: Array<CalendarEventCategoryConfig>
	eventReminders: Array<SystemConfigReminder>
	countries: Array<SystemConfigCountryData>
	eventStatuses: Array<CalendarEventStatusConfig>
	martialStatus: Array<SystemConfigUIText>
	diabetesType: Array<
		SystemConfigUIName<DIABETES_TYPES, keyof typeof DIABETES_TYPES>
	>
	insulinTreatment: Array<SystemConfigUIName>
	smoking: Array<SystemConfigUIText>
	comorbiditiesOpts: Array<
		SystemConfigValueParam<COMORBIDITIES, COMORBIDITIES>
	>
	roles: Array<SystemConfigRole>
	roleTypes: Array<SystemConfigRoleType>
	languageRegion: Array<SystemConfigLanguage>
	carbs: Array<SystemConfigCarbs>
	timezones: Array<TimeZones>
	gender: Array<SystemConfigGenders>
	age: Array<SystemConfigAgeGroups>
	taskStatusesV2: SystemTaskStatusesV2
}

export interface TimeZones {
	label: string
	offset: string
	tzCode: string
}

export interface SystemConfiguration {
	accountsRef: Array<SystemConfigAccountRef>
	sms: SystemConfigSms
	skills: Array<SystemConfigSkills>
	expertises: SystemConfigExpertise[]
	userTitle: SystemConfigUserTitle[]
	eventCategoriesV2: Array<CalendarEventCategoryConfigV2>
}

export interface SystemConfigDropdowns {
	[key: string]: unknown
	['report_type']: ReportType[]
	['reports_period']: ReportPeriod[]
}

export interface SystemConfig {
	loading: boolean
	error: string | null
	system: SystemConfigSystem
	uiTabs: object
	configuration: SystemConfiguration
	about: object
	dropDowns: SystemConfigDropdowns
	templates: SystemConfigTemplates
	userConfig: any | null
}

export interface SystemConfigReminder {
	id: string
	value: REMINDER_VALUES
	display: string
}

export interface SystemConfigBaseParam {
	id: string
	['ui_name']: string
}

export interface SystemConfigUIText {
	id: string
	['ui_text']: string
}
export interface SystemConfigCarbs extends SystemConfigUIText {}

export interface SystemConfigUIName<T = string, P = string> {
	id: T
	['ui_name']: P
}

export interface SystemConfigValueParam<T = string, P = string> {
	id: T
	value: P
}

export interface SystemConfigTemplates {
	checklist: Array<SystemConfigTemplatesItem>
}

export interface SystemConfigSms {
	templates: Array<SystemConfigSmsTemplate>
	languages: Array<SystemConfigSmsLanguage>
}

export interface SystemTaskStatusesV2 {
	externalClinic: Array<
		SystemConfigValueParam<number, TASK_STATUS_FOR_EXTERNAL_CLINIC>
	>
	internalClinic: Array<
		SystemConfigValueParam<number, TASK_STATUS_FOR_INTERNAL_CLINIC>
	>
}

export interface SystemConfigExpertise extends SystemConfigBaseParam {}
export interface SystemConfigUserTitle extends SystemConfigBaseParam {}
export interface SystemConfigRole extends SystemConfigBaseParam {
	name: string
}
export interface SystemConfigRoleType extends SystemConfigBaseParam {
	name: string
	tier: number
}

export interface SystemConfigLanguage {
	id: string
	['ui_text']: Uppercase<LANGUAGES_FULL>
	name: Lowercase<keyof typeof LANGUAGES_FULL>
}

export interface SystemConfigGenders {
	id: string
	['ui_text']: GENDERS
	name: GENDERS
}

export interface SystemConfigAgeGroups {
	id: string
	['ui_name']: AGE_GROUPS
}

export interface SystemConfigSmsTemplate {
	[key: string]: any
	name: string
	en: string
	fr?: string
	es?: string
}

export interface SystemConfigSmsLanguage {
	[key: string]: any
	id: string
	value: string
}

export interface SystemConfigTemplatesItemSection {
	id: string
	items: Array<SystemConfigTemplatesItemSectionItem>
	title: string
}

export interface SystemConfigTemplatesItemSectionItemText {
	id: string
	placeholder: string
}

// maybe, should update name to default template.Should check it, when we wil get out next template
export interface SystemConfigTemplatesItemSectionItem {
	id: string
	label: string
	checkbox?: boolean
	text?: Array<SystemConfigTemplatesItemSectionItemText>
	header?: string
	span?: string
}

export interface SystemConfigTemplatesItem {
	sections: Array<SystemConfigTemplatesItemSection>
	name: string
}

export interface SystemConfigAccountRef {
	id: string
	['ui_text']: string
	image: string
}

export interface SystemConfigSkills {
	id: string
	['ui_name']: string
}

/**
 * System config redux action types
 */

export enum SystemConfigActionTypes {
	SYSTEM_CONFIGS_GET = 'SYSTEM_CONFIGS_GET',
	USER_CONFIG_DATA_GET = 'USER_CONFIG_DATA_GET',
}

interface SystemConfigSet {
	type: SystemConfigActionTypes.SYSTEM_CONFIGS_GET
	payload: SystemConfig
}

export interface SystemConfigOrganizationType {
	id: string
	value: string
}
export interface SystemConfigOrganizationStatus {
	id: string
	value: string
}
interface SetUserConfigData {
	type: SystemConfigActionTypes.USER_CONFIG_DATA_GET
	payload: any
}

export type SystemSetConfigsAction = SystemConfigSet | SetUserConfigData
